/**
 * Concrete helpers for the HTTP side of a chat client: the turn-mode
 * header, the status and transcript routes' answers, and the retry and reconnect policies
 * the manual prescribes. Applications keep their own fetch calls and routes.
 */
import { isBusyRefusal } from './errors.js';
import { hasUnfinishedTurn, isObject } from './events.js';
export const TURN_MODE_HEADER = 'X-Agent-Runtime-Turn-Mode';
export function parseTurnMode(value) {
    return value === 'started' || value === 'steered' ? value : null;
}
export function turnModeFromHeaders(headers, name = TURN_MODE_HEADER) {
    if (typeof headers.get === 'function')
        return parseTurnMode(headers.get(name));
    const wanted = name.toLowerCase();
    for (const [key, value] of Object.entries(headers)) {
        if (key.toLowerCase() === wanted)
            return parseTurnMode(value);
    }
    return null;
}
const STATUS_VALUES = new Set([
    'pending',
    'running',
    'completed',
    'failed',
    'cancelled',
    'missing',
    'recoverable',
]);
export function sessionStatusFromPayload(payload) {
    const raw = (payload ?? {});
    const status = raw.status;
    if (typeof status !== 'string' || !STATUS_VALUES.has(status)) {
        throw new Error(`Unknown session status: ${String(status)}`);
    }
    const active = Boolean(raw.active);
    const recoverable = Boolean(raw.recoverable);
    const state = active ? 'active' : recoverable ? 'recoverable' : status === 'missing' ? 'missing' : 'terminal';
    return { status: status, active, recoverable, state };
}
export const DEFAULT_TRANSCRIPT_TURNS = 30;
export const MAX_TRANSCRIPT_TURNS = 100;
export function transcriptPageFromPayload(payload) {
    const raw = (payload ?? {});
    const events = raw.events;
    if (!Array.isArray(events) || !events.every(isObject)) {
        throw new Error('Transcript page has no events list');
    }
    const page = raw.page;
    if (!isObject(page))
        throw new Error('Transcript page has no page cursor');
    const beforeUuid = typeof page.before_uuid === 'string' && page.before_uuid ? page.before_uuid : null;
    const lastUuid = typeof raw.last_uuid === 'string' && raw.last_uuid ? raw.last_uuid : null;
    return {
        events: events,
        beforeUuid,
        hasMore: Boolean(page.has_more),
        lastUuid,
        status: sessionStatusFromPayload(payload),
    };
}
/** Query string for a transcript page; the backend forwards it to the runner. */
export function transcriptQuery(options = {}) {
    const query = new URLSearchParams();
    if (options.beforeUuid)
        query.set('before_uuid', options.beforeUuid);
    if (options.limit !== undefined) {
        if (!Number.isInteger(options.limit) || options.limit < 1 || options.limit > MAX_TRANSCRIPT_TURNS) {
            throw new Error(`limit must be between 1 and ${MAX_TRANSCRIPT_TURNS}`);
        }
        query.set('limit', String(options.limit));
    }
    return query;
}
/** What a client that lost its stream does next: tail the live turn, replay the transcript, or nothing. */
export function resumePlan(status) {
    if (status.active)
        return 'tail';
    return status.status === 'missing' ? 'idle' : 'replay';
}
/** Whether a replayed transcript needs a live tail: its last turn is open or the runner says so. */
export function shouldResumeTurn(events, active) {
    return active || hasUnfinishedTurn(events);
}
export const DEFAULT_RECONNECT = { baseMs: 500, maxMs: 8000, attempts: 8 };
/** Delay before reconnect attempt `attempt` (1-based); `null` when attempts are spent. */
export function reconnectDelay(attempt, policy = DEFAULT_RECONNECT) {
    if (attempt < 1 || attempt > policy.attempts)
        return null;
    return Math.min(policy.baseMs * 2 ** (attempt - 1), policy.maxMs);
}
export const CONFLICT_RETRY_DELAYS_MS = [100, 200, 300];
/**
 * True when a 409 explicitly says `busy` (structured or legacy detail); the
 * body is read from a clone. A 409 whose body is missing, malformed, or
 * carries any other code is never transient.
 */
async function isTransientBusy(response) {
    if (response.status !== 409)
        return false;
    let body = null;
    try {
        body = await response.clone().json();
    }
    catch {
        return false;
    }
    return isBusyRefusal(response.status, body);
}
function abortError(signal) {
    return signal.reason ?? new DOMException('The operation was aborted.', 'AbortError');
}
/** Resolves after `ms`, or rejects with the signal's reason as soon as it aborts. */
function abortableTimeout(ms, signal) {
    return new Promise((resolve, reject) => {
        if (signal?.aborted) {
            reject(abortError(signal));
            return;
        }
        const onAbort = () => {
            clearTimeout(timer);
            reject(abortError(signal));
        };
        const timer = setTimeout(() => {
            signal?.removeEventListener('abort', onAbort);
            resolve();
        }, ms);
        signal?.addEventListener('abort', onAbort, { once: true });
    });
}
/**
 * Re-issue a request that answered an explicit `busy` 409 a few times: right
 * after `end`, the runner can still hold the turn for a moment. Every other
 * 409 (`harness_mismatch`, `request_already_admitted`, a code this client
 * does not know, an empty or malformed body) is final and comes back at
 * once. The returned response's body is unread. An aborted `signal` stops
 * the backoff and rejects with its reason without issuing another request.
 */
export async function retryConflictResponses(request, wait = abortableTimeout, delays = CONFLICT_RETRY_DELAYS_MS, signal) {
    if (signal?.aborted)
        throw abortError(signal);
    let response = await request();
    for (const delay of delays) {
        if (!(await isTransientBusy(response)))
            break;
        if (signal?.aborted)
            throw abortError(signal);
        await wait(delay, signal);
        if (signal?.aborted)
            throw abortError(signal);
        response = await request();
    }
    return response;
}
/**
 * Rate limiter for prewarm calls: `true` when a conversation should prewarm
 * now, `false` while an earlier prewarm is still fresh.
 */
export function createPrewarmGate(intervalMs = 5 * 60_000) {
    const last = new Map();
    return (key, now = Date.now()) => {
        const previous = last.get(key);
        if (previous !== undefined && now - previous < intervalMs)
            return false;
        last.set(key, now);
        return true;
    };
}
